import { getNotasLocal, getListasLocal, getShoppingListNotasLocal, getNotasShoppingListsLocal } from '../db.js';
import { escapeHTML } from '../security.js';
import { authManager, getSavedScope, setSavedScope } from '../auth-manager.js';
import { renderPeriodFilterHTML, initPeriodFilter, filterNotasByPeriod, getSavedPeriodFilter } from '../period-filter.js';
import {
  getSuggestedShoppingLists,
  linkNotaToShoppingList,
  unlinkNotaFromShoppingList,
  openLinkToListModal,
  calculateTemporalProximity
} from '../utils/reconciliation.js';

export async function renderNotasView(container, param, syncManager) {
  let currentScope = getSavedScope();
  const savedPeriod = getSavedPeriodFilter();
  let notas = [];
  try {
    if (authManager.isAuthenticated()) {
      const res = await authManager.fetchWithAuth(`/api/notas?scope=${currentScope}&full=true`);
      if (res.ok) {
        notas = await res.json();
      } else {
        notas = await getNotasLocal();
      }
    } else {
      notas = await getNotasLocal();
    }
  } catch (e) {
    notas = await getNotasLocal();
  }

  const isPersonal = currentScope === 'personal';
  const isFamily = currentScope === 'family';

  container.innerHTML = `
    <section class="section-header" style="margin-bottom: 1.25rem;">
      <div style="display: flex; align-items: center; justify-content: space-between; gap: 0.75rem;">
        <div>
          <h2 class="section-title" style="margin: 0; font-size: clamp(1.25rem, 4.5vw, 1.6rem); font-weight: 800; color: #111827; line-height: 1.2;">Minhas Notas</h2>
          <p class="section-subtitle" id="notas-count-subtitle" style="margin: 0.15rem 0 0 0; font-size: 0.82rem; color: #64748B;">${notas.length} ${notas.length === 1 ? 'nota cadastrada' : 'notas cadastradas'}</p>
        </div>
        <div class="scope-selector" id="scope-selector">
          <button class="scope-pill ${isPersonal ? 'active' : ''}" data-scope="personal">Pessoal</button>
          <button class="scope-pill ${isFamily ? 'active' : ''}" data-scope="family">Família</button>
        </div>
      </div>
    </section>

    <section class="search-bar">
      <span class="material-symbols-outlined search-icon">search</span>
      <input type="text" id="search-input" class="search-input" placeholder="Buscar por estabelecimento..." />
    </section>

    ${renderPeriodFilterHTML(savedPeriod.period, savedPeriod.customLabel, 'notas-custom-period-modal')}

    <!-- Summary Carousel Cards -->
    <div id="notas-kpi-summary" class="home-carousel-wrapper" style="margin-top: 0.85rem; margin-bottom: 1.25rem;"></div>

    <section id="notas-list" class="space-y-gutter"></section>
  `;

  const searchInput = container.querySelector('#search-input');
  const scopeSelector = container.querySelector('#scope-selector');
  const listContainer = container.querySelector('#notas-list');
  const countSubtitle = container.querySelector('#notas-count-subtitle');
  const kpiSummaryContainer = container.querySelector('#notas-kpi-summary');

  let currentPeriod = savedPeriod.period;
  let customStart = savedPeriod.customStart;
  let customEnd = savedPeriod.customEnd;
  let currentSearch = '';

  // Scope toggle listener
  scopeSelector.querySelectorAll('.scope-pill').forEach(btn => {
    btn.addEventListener('click', async () => {
      scopeSelector.querySelectorAll('.scope-pill').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');

      currentScope = btn.getAttribute('data-scope');
      setSavedScope(currentScope);

      try {
        if (authManager.isAuthenticated()) {
          const res = await authManager.fetchWithAuth(`/api/notas?scope=${currentScope}&full=true`);
          if (res.ok) {
            notas = await res.json();
          } else {
            notas = await getNotasLocal();
          }
        } else {
          notas = await getNotasLocal();
        }
      } catch (e) {
        notas = await getNotasLocal();
      }
      filterAndRender();
    });
  });

  // Global Scope synchronization listener
  window.addEventListener('nfce-scope-changed', async (e) => {
    const newScope = e.detail?.scope;
    if (newScope && newScope !== currentScope) {
      currentScope = newScope;
      if (scopeSelector) {
        scopeSelector.querySelectorAll('.scope-pill').forEach(b => {
          b.classList.toggle('active', b.getAttribute('data-scope') === currentScope);
        });
      }
      try {
        if (authManager.isAuthenticated()) {
          const res = await authManager.fetchWithAuth(`/api/notas?scope=${currentScope}&full=true`);
          if (res.ok) {
            notas = await res.json();
          } else {
            notas = await getNotasLocal();
          }
        } else {
          notas = await getNotasLocal();
        }
      } catch (e) {
        notas = await getNotasLocal();
      }
      filterAndRender();
    }
  });

  async function filterAndRender() {
    // Filter by period (1 Ano, 3 Meses, Este Mês, Esta Semana, Hoje, Personalizado)
    let filtered = filterNotasByPeriod(notas, currentPeriod, customStart, customEnd);

    // Filter by store search
    if (currentSearch) {
      const q = currentSearch.toLowerCase();
      filtered = filtered.filter(n =>
        (n.emitente?.razaoSocial || n.razaoSocial || '').toLowerCase().includes(q)
      );
    }

    if (countSubtitle) {
      countSubtitle.textContent = `${filtered.length} ${filtered.length === 1 ? 'nota encontrada' : 'notas encontradas'}`;
    }

    // Render KPI Cards (Total Gasto, Ticket Médio, Total de Notas)
    const totalGasto = filtered.reduce((sum, n) => sum + (n.totais?.valorTotal || n.valorTotal || 0), 0);
    const avgTicket = filtered.length > 0 ? (totalGasto / filtered.length) : 0;
    const totalNotasCount = filtered.length;

    if (kpiSummaryContainer) {
      kpiSummaryContainer.innerHTML = `
        <div class="home-carousel-container">
          
          <!-- 1. Total Gasto -->
          <div class="home-kpi-card">
            <div class="home-card-header">
              <span class="home-card-label">Total Gasto</span>
              <span class="material-symbols-outlined" style="font-size: 18px; color: var(--color-primary-text, #2563EB);">payments</span>
            </div>
            <div class="home-card-value font-mono money-val" style="font-size: 1.45rem; font-weight: 800; color: var(--text-main);">
              R$ ${totalGasto.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div class="home-card-footer">
              <span class="home-card-sublabel">No período selecionado</span>
            </div>
          </div>

          <!-- 2. Ticket Médio -->
          <div class="home-kpi-card">
            <div class="home-card-header">
              <span class="home-card-label">Ticket Médio</span>
              <span class="material-symbols-outlined" style="font-size: 18px; color: #D97706;">receipt_long</span>
            </div>
            <div class="home-card-value font-mono money-val" style="font-size: 1.45rem; font-weight: 800; color: #D97706;">
              R$ ${avgTicket.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div class="home-card-footer">
              <span class="home-card-sublabel">Média por cupom fiscal</span>
            </div>
          </div>

          <!-- 3. Total de Notas -->
          <div class="home-kpi-card">
            <div class="home-card-header">
              <span class="home-card-label">Notas Fiscais</span>
              <span class="material-symbols-outlined" style="font-size: 18px; color: #10B981;">receipt</span>
            </div>
            <div class="home-card-value font-mono" style="font-size: 1.6rem; font-weight: 800; color: #10B981;">
              ${totalNotasCount}
            </div>
            <div class="home-card-footer">
              <span class="home-card-sublabel">${totalNotasCount === 1 ? 'cupom registrado' : 'cupons registrados'}</span>
            </div>
          </div>

        </div>
      `;
    }

    // Sort by emission date desc
    filtered.sort((a, b) => {
      const dateA = a.dataEmissao ? new Date(a.dataEmissao).getTime() : 0;
      const dateB = b.dataEmissao ? new Date(b.dataEmissao).getTime() : 0;
      return dateB - dateA;
    });

    const dismissedSuggestions = new Set();
    let openLists = [];
    let linksByNota = new Map();

    async function loadShoppingListsData() {
      try {
        const [personalLists, familyLists] = await Promise.all([
          getListasLocal('personal').catch(() => []),
          getListasLocal('family').catch(() => [])
        ]);
        const uniqueLists = new Map();
        [...personalLists, ...familyLists].forEach(l => uniqueLists.set(Number(l.id), l));
        openLists = Array.from(uniqueLists.values()).filter(l => l.status === 'aberta' || !l.status);

        // Build links map
        linksByNota.clear();

        // 1. From list.linkedNotas
        for (const list of uniqueLists.values()) {
          if (list.linkedNotas && Array.isArray(list.linkedNotas)) {
            list.linkedNotas.forEach(ln => {
              const nId = Number(ln.notaId);
              if (!linksByNota.has(nId)) linksByNota.set(nId, []);
              if (!linksByNota.get(nId).some(x => Number(x.id) === Number(list.id))) {
                linksByNota.get(nId).push({ id: list.id, name: list.name, scope: list.scope });
              }
            });
          }
        }

        // 2. From IndexedDB local links store
        for (const n of filtered) {
          const localLinks = await getNotasShoppingListsLocal(n.id).catch(() => []);
          for (const link of localLinks) {
            const list = uniqueLists.get(Number(link.shoppingListId));
            const nId = Number(n.id);
            if (!linksByNota.has(nId)) linksByNota.set(nId, []);
            if (list && !linksByNota.get(nId).some(x => Number(x.id) === Number(list.id))) {
              linksByNota.get(nId).push({ id: list.id, name: list.name, scope: list.scope });
            }
          }
        }
      } catch (e) {
        console.warn('Error loading shopping lists for reconciliation:', e);
      }
    }

    await loadShoppingListsData();

    function renderCards() {
      if (filtered.length === 0) {
        listContainer.innerHTML = `
          <div class="empty-state">
            <span class="material-symbols-outlined empty-icon">receipt_long</span>
            <div class="empty-title">Nenhuma nota encontrada</div>
            <div class="empty-desc">Nenhum cupom fiscal encontrado para o período ou busca selecionados.</div>
          </div>
        `;
        return;
      }

      listContainer.innerHTML = filtered.map(n => {
        const storeName = n.emitente?.razaoSocial || n.razaoSocial || 'Estabelecimento';
        const valor = (n.totais?.valorTotal || n.valorTotal || 0).toFixed(2).replace('.', ',');
        const qtd = n.totais?.qtdItens || n.qtdItens || (n.itens ? n.itens.length : 0);
        const dataStr = n.dataEmissao ? new Date(n.dataEmissao).toLocaleDateString('pt-BR', {
          day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
        }) : 'Data N/A';

        const isPendingCompat = n.status === 'pendente_compatibilidade';
        const linkedLists = linksByNota.get(Number(n.id)) || [];
        const hasLinks = linkedLists.length > 0;

        // Check for temporal suggestion if not linked and not dismissed
        let suggestedList = null;
        if (!hasLinks && !dismissedSuggestions.has(Number(n.id)) && openLists.length > 0) {
          for (const list of openLists) {
            const { score, isWithinWindow } = calculateTemporalProximity(n.dataEmissao, list.updatedAt || list.createdAt);
            if (isWithinWindow && score >= 0.75) {
              suggestedList = list;
              break;
            }
          }
        }

        return `
          <article class="glass-panel nota-card" data-id="${n.id}" style="${isPendingCompat ? 'border-left: 4px solid #F59E0B;' : ''}">
            <!-- Main Row -->
            <div class="nota-card-main">
              <div class="nota-card-icon" style="${isPendingCompat ? 'background: rgba(245, 158, 11, 0.15); color: #F59E0B;' : ''}">
                <span class="material-symbols-outlined">${isPendingCompat ? 'hourglass_top' : 'shopping_cart'}</span>
              </div>
              <div class="nota-card-info">
                <div style="display: flex; align-items: center; gap: 0.4rem; margin-bottom: 0.2rem; flex-wrap: wrap;">
                  <h3 class="nota-card-title" style="margin: 0;">${escapeHTML(storeName)}</h3>
                  ${isPendingCompat ? `<span style="font-size: 0.68rem; font-weight: 700; background: rgba(245, 158, 11, 0.2); color: #F59E0B; border: 1px solid rgba(245, 158, 11, 0.3); padding: 0.1rem 0.4rem; border-radius: 0.4rem;">Em Análise</span>` : ''}
                </div>
                <div class="nota-card-date">${escapeHTML(dataStr)}</div>
              </div>
              <div class="nota-card-meta">
                <div class="nota-card-value">R$ ${valor}</div>
                <div class="nota-card-count">${qtd} ${qtd === 1 ? 'item' : 'itens'}</div>
              </div>
            </div>

            <!-- Linha de Baixo (Condicional) -->
            ${hasLinks ? `
              <!-- Caso 1: Já Vinculada a Lista(s) -->
              <div class="nota-card-links" style="display: flex; align-items: center; justify-content: space-between; width: 100%; padding-top: 0.4rem; border-top: 1px solid var(--border-color-subtle, rgba(0,0,0,0.05)); gap: 0.5rem; flex-wrap: wrap;">
                <div style="display: flex; align-items: center; gap: 0.35rem; flex-wrap: wrap;">
                  ${linkedLists.map(l => `
                    <span class="badge-linked-list" data-list-id="${l.id}" title="Ver lista de compras" style="font-size: 0.74rem; font-weight: 700; background: rgba(16, 185, 129, 0.12); color: #10B981; border: 1px solid rgba(16, 185, 129, 0.25); padding: 0.2rem 0.6rem; border-radius: var(--radius-pill); display: inline-flex; align-items: center; gap: 0.35rem; cursor: pointer;">
                      <span class="material-symbols-outlined" style="font-size: 14px;">playlist_add_check</span>
                      <span>${escapeHTML(l.name)}</span>
                    </span>
                  `).join('')}
                </div>
                <button class="btn-open-link-modal" data-nota-id="${n.id}" title="Gerenciar ou alterar vínculo" style="background: none; border: none; color: var(--text-muted); font-size: 0.72rem; font-weight: 600; display: inline-flex; align-items: center; gap: 0.2rem; cursor: pointer; padding: 0.15rem 0.35rem; border-radius: 0.35rem;">
                  <span class="material-symbols-outlined" style="font-size: 14px;">tune</span>
                  <span>Alterar</span>
                </button>
              </div>
            ` : (suggestedList ? `
              <!-- Caso 2: Sugestão Temporal -->
              <div class="nota-card-suggestion" style="background: rgba(37, 99, 235, 0.08); border: 1px solid rgba(37, 99, 235, 0.2); border-radius: 0.65rem; padding: 0.35rem 0.65rem; display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; width: 100%; box-sizing: border-box;">
                <div style="display: flex; align-items: center; gap: 0.35rem; font-size: 0.75rem; color: #2563EB; font-weight: 700; min-width: 0; flex: 1;">
                  <span class="material-symbols-outlined" style="font-size: 15px; color: #F59E0B; flex-shrink: 0;">lightbulb</span>
                  <span style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">Vincular a <strong>${escapeHTML(suggestedList.name)}</strong>?</span>
                </div>
                <div style="display: flex; align-items: center; gap: 0.35rem; flex-shrink: 0;">
                  <button class="btn-quick-link" data-nota-id="${n.id}" data-list-id="${suggestedList.id}" style="padding: 0.2rem 0.55rem; background: #2563EB; color: #FFFFFF; border: none; border-radius: var(--radius-pill); font-size: 0.72rem; font-weight: 700; cursor: pointer; display: flex; align-items: center; gap: 0.2rem;">
                    Vincular
                  </button>
                  <button class="btn-dismiss-suggestion" data-nota-id="${n.id}" title="Ignorar sugestão" style="padding: 0.2rem 0.35rem; background: transparent; color: var(--text-muted); border: none; border-radius: var(--radius-pill); font-size: 0.75rem; cursor: pointer;">✕</button>
                </div>
              </div>
            ` : `
              <!-- Caso 3: Não Vinculada e Sem Sugestão Automática -->
              <div style="display: flex; align-items: center; justify-content: space-between; width: 100%; padding-top: 0.35rem; border-top: 1px solid var(--border-color-subtle, rgba(0,0,0,0.05));">
                <button class="btn-open-link-modal" data-nota-id="${n.id}" style="background: none; border: none; color: var(--text-muted); font-size: 0.74rem; font-weight: 600; display: inline-flex; align-items: center; gap: 0.3rem; cursor: pointer; padding: 0.1rem 0.2rem; border-radius: 0.35rem; transition: color 0.15s ease;">
                  <span class="material-symbols-outlined" style="font-size: 15px; color: #3B82F6;">link</span>
                  <span>Vincular a uma Lista</span>
                </button>
                <span class="material-symbols-outlined" style="font-size: 15px; color: var(--text-muted); opacity: 0.5;">chevron_right</span>
              </div>
            `)}
          </article>
        `;
      }).join('');

      // Attach click listeners
      listContainer.querySelectorAll('.nota-card').forEach(card => {
        card.addEventListener('click', (e) => {
          if (e.target.closest('button') || e.target.closest('.badge-linked-list')) return;
          const id = card.getAttribute('data-id');
          window.location.hash = `#nota/${id}`;
        });
      });

      // Quick link button
      listContainer.querySelectorAll('.btn-quick-link').forEach(btn => {
        btn.addEventListener('click', async (e) => {
          e.stopPropagation();
          const notaId = btn.getAttribute('data-nota-id');
          const listId = btn.getAttribute('data-list-id');
          btn.disabled = true;
          btn.innerHTML = `<span class="material-symbols-outlined spin" style="font-size: 12px;">sync</span>`;
          await linkNotaToShoppingList(listId, notaId, 'temporal_suggestion', 0.9);
          await loadShoppingListsData();
          renderCards();
        });
      });

      // Dismiss suggestion button
      listContainer.querySelectorAll('.btn-dismiss-suggestion').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          const notaId = Number(btn.getAttribute('data-nota-id'));
          dismissedSuggestions.add(notaId);
          renderCards();
        });
      });

      // Open link modal button
      listContainer.querySelectorAll('.btn-open-link-modal').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          const notaId = Number(btn.getAttribute('data-nota-id'));
          const nota = filtered.find(n => Number(n.id) === notaId);
          if (nota) {
            openLinkToListModal({
              nota,
              onLinked: async () => {
                await loadShoppingListsData();
                renderCards();
              }
            });
          }
        });
      });

      // Click on linked list badge navigates to that list
      listContainer.querySelectorAll('.badge-linked-list').forEach(badge => {
        badge.addEventListener('click', (e) => {
          e.stopPropagation();
          const listId = badge.getAttribute('data-list-id');
          window.location.hash = `#lista/${listId}`;
        });
      });
    }

    renderCards();
  }

  searchInput.addEventListener('input', (e) => {
    currentSearch = e.target.value.trim();
    filterAndRender();
  });

  // Initialize Period Filter Component
  initPeriodFilter(container, {
    initialPeriod: savedPeriod.period,
    initialCustomStart: savedPeriod.customStart,
    initialCustomEnd: savedPeriod.customEnd,
    initialCustomLabel: savedPeriod.customLabel,
    modalId: 'notas-custom-period-modal',
    onFilterChange: (period, start, end) => {
      currentPeriod = period;
      customStart = start;
      customEnd = end;
      filterAndRender();
    }
  });

  filterAndRender();

  // Listen for background tab resume sync update
  const handleSyncUpdate = async () => {
    const currentHash = window.location.hash || '#home';
    const route = currentHash.split('/')[0].replace('#', '');
    if (route !== 'notas' && route !== 'nota') {
      window.removeEventListener('datasyncupdated', handleSyncUpdate);
      return;
    }
    try {
      if (authManager.isAuthenticated()) {
        const res = await authManager.fetchWithAuth(`/api/notas?scope=${currentScope}&full=true`);
        if (res.ok) {
          const checkHash = window.location.hash || '#home';
          const checkRoute = checkHash.split('/')[0].replace('#', '');
          if (checkRoute === 'notas') {
            notas = await res.json();
            filterAndRender();
          }
        }
      }
    } catch (e) {}
  };
  window.addEventListener('datasyncupdated', handleSyncUpdate);
}
